import {
  Component,
  computed,
  effect,
  forwardRef,
  input,
  OnChanges,
  output,
  SimpleChanges,
  signal,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";

export interface FormAutocompleteOption<T = any> {
  value: T;
  label: string;
  count?: number;
  disabled?: boolean;
}

@Component({
  selector: "fh-form-autocomplete",
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: "./form-autocomplete.component.html",
  styleUrls: ["./form-autocomplete.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAutocompleteComponent),
      multi: true,
    },
  ],
})
export class FormAutocompleteComponent<T = any> implements ControlValueAccessor, OnChanges {
  // Inputs
  options = input.required<FormAutocompleteOption<T>[]>();
  label = input<string>("");
  placeholder = input<string>("Select...");
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  showCounts = input<boolean>(false);
  appearance = input<"fill" | "outline">("fill");

  // Outputs
  change = output<T | null>();
  selectionChange = output<T | null>();

  // Internal state
  searchText = signal<string>("");
  private _value: T | null = null;
  private _disabled = false;
  private _isOpen = false; // Track if dropdown is open

  // Callbacks for ControlValueAccessor
  private onChange: (value: T | null) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed properties
  filteredOptions = computed(() => {
    const search = this.searchText().toLowerCase();
    const opts = this.options();

    // If dropdown is open and search is the display value, show all options
    if (this._isOpen && this._value !== null && this._value !== undefined) {
      const selectedOption = opts.find((opt) => opt.value === this._value);
      if (selectedOption && search === selectedOption.label.toLowerCase()) {
        return opts; // Show all options when dropdown opens with selected value
      }
    }

    if (!search) {
      return opts;
    }

    return opts.filter((option) => option.label.toLowerCase().includes(search));
  });

  constructor() {
    // Use effect to watch for options changes and update display
    effect(
      () => {
        const opts = this.options();
        if (opts.length > 0 && this._value !== null && this._value !== undefined) {
          const option = opts.find((opt) => opt.value === this._value);
          if (option && this.searchText() !== option.label) {
            this.searchText.set(option.label);
          }
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this._disabled = this.disabled();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: T | null): void {
    this._value = value;
    if (value === null || value === undefined) {
      this.searchText.set("");
    } else {
      const option = this.options().find((opt) => opt.value === value);
      if (option) {
        this.searchText.set(option.label);
      }
    }
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText.set(value);
  }

  onSearchChange(): void {
    // User is typing, so we're searching
    // Don't set _isOpen here, let the panel events handle it
  }

  onFocus(): void {
    // Focus event - panel will open and trigger onPanelOpen
  }

  onPanelOpen(): void {
    // Panel actually opened - now we can show all options
    this._isOpen = true;
  }

  onPanelClose(): void {
    // Panel closed
    this._isOpen = false;
  }

  onSelectionChange(value: T | null): void {
    this._value = value;
    // Don't set _isOpen here - onPanelClose will handle it

    if (value === null || value === undefined) {
      this.searchText.set("");
    } else {
      const option = this.options().find((opt) => opt.value === value);
      if (option) {
        this.searchText.set(option.label);
      }
    }

    this.onChange(value);
    this.onTouched();
    this.change.emit(value);
    this.selectionChange.emit(value);
  }

  clearSelection(): void {
    this.searchText.set("");
    this._value = null;
    this.onChange(null);
    this.onTouched();
    this.change.emit(null);
    this.selectionChange.emit(null);
  }

  displayFn(value: T | null): string {
    if (value === null || value === undefined) {
      return "";
    }
    const option = this.options().find((opt) => opt.value === value);
    return option ? option.label : "";
  }

  get isDisabled(): boolean {
    return this._disabled;
  }

  get hasSelection(): boolean {
    return this._value !== null && this._value !== undefined;
  }
}
